/**
 * Learn hub URLs. The interactive lesson tracks keep /academy/tracks, /academy/glossary and
 * /academy/:track(/:lesson). Loaded by the Vite config too, hence the .ts import extensions.
 */
import type { CategoryId, CourseId, TopicId } from './taxonomy.ts';

export type LearnRoute =
  | { kind: 'home' }
  | { kind: 'articles' }
  | { kind: 'category'; id: CategoryId }
  | { kind: 'topic'; id: TopicId }
  | { kind: 'course'; id: CourseId }
  | { kind: 'article'; slug: string };

const SECTIONS = ['articles', 'article', 'category', 'topic', 'course'] as const;

/** True for pages of the public Learn hub (no sign-in). */
export function isLearnPath(path: string): boolean {
  const p = path.replace(/\/+$/, '') || '/';
  if (p === '/academy') return true;
  const [, a, b, c, ...rest] = p.split('/');
  if (a !== 'academy' || rest.length) return false;
  if (b === 'articles') return c === undefined;
  return (SECTIONS as readonly string[]).includes(b ?? '') && !!c;
}

/** The route for a Learn hub path; ids are checked by the page, not here. */
export function parseLearnRoute(path: string): LearnRoute | null {
  if (!isLearnPath(path)) return null;
  const [, , b, c] = (path.replace(/\/+$/, '') || '/').split('/');
  const id = c ? decodeURIComponent(c) : '';
  switch (b) {
    case undefined:
      return { kind: 'home' };
    case 'articles':
      return { kind: 'articles' };
    case 'category':
      return { kind: 'category', id: id as CategoryId };
    case 'topic':
      return { kind: 'topic', id: id as TopicId };
    case 'course':
      return { kind: 'course', id: id as CourseId };
    default:
      return { kind: 'article', slug: id };
  }
}

export const learnPath = {
  home: () => '/academy',
  articles: (q?: Record<string, string>) => {
    const s = new URLSearchParams(Object.entries(q ?? {}).filter(([, v]) => v)).toString();
    return `/academy/articles${s ? `?${s}` : ''}`;
  },
  category: (id: string) => `/academy/category/${id}`,
  topic: (id: string) => `/academy/topic/${id}`,
  course: (id: string) => `/academy/course/${id}`,
  article: (slug: string) => `/academy/article/${slug}`,
};

export function routePath(r: LearnRoute): string {
  switch (r.kind) {
    case 'home':
      return learnPath.home();
    case 'articles':
      return learnPath.articles();
    case 'category':
      return learnPath.category(r.id);
    case 'topic':
      return learnPath.topic(r.id);
    case 'course':
      return learnPath.course(r.id);
    case 'article':
      return learnPath.article(r.slug);
  }
}

/** Where the interactive lesson tracks (quizzes, demo practice, Earn quests) now start. */
export const TRACKS_PATH = '/academy/tracks';
